import { Injectable, computed, inject, signal } from '@angular/core';
import { Observable, tap } from 'rxjs';
import { ApiService, ApiResponse } from './api.service';

export interface SubscriptionPlan {
  key: string;
  audience?: 'individual' | 'company' | 'any';
  name: string;
  price_monthly_cents: number;
  price_per_user_cents?: number;
  included_users?: number;
  currency: string;
  max_users: number | null;
  has_advanced_roadmaps: boolean;
  has_time_tracking: boolean;
  description?: string;
  max_projects?: number | null;
}

export type SubscriptionStatus =
  | 'owned'
  | 'trialing'
  | 'active'
  | 'past_due'
  | 'suspended'
  | 'cancelled'
  | 'unknown';

export interface SubscriptionSnapshot {
  site: { id: number; key: string; name: string; subdomain: string; account_type: 'individual' | 'company' };
  status: SubscriptionStatus;
  is_owned: boolean;
  read_only: boolean;
  plan: SubscriptionPlan;
  billing?: {
    per_user_cents: number;
    base_cents: number;
    included_users: number;
    billable_users: number;
    monthly_total_cents: number;
    currency: string;
  };
  trial_ends_at: string | null;
  trial_days_left: number | null;
  grace_until: string | null;
  current_period_end: string | null;
  usage: { seats_used: number; seats_limit: number | null };
}

export interface InvoiceItem {
  id: number;
  description: string;
  quantity: number;
  unit_price_cents: number;
  amount_cents: number;
}

export interface Invoice {
  id: number;
  invoice_number: string;
  status: string;
  reason: string | null;
  currency: string;
  plan_key: string | null;
  seats_billed: number;
  subtotal_cents: number;
  total_cents: number;
  period_start: string | null;
  period_end: string | null;
  bill_to_name: string | null;
  bill_to_email: string | null;
  emailed_at: string | null;
  email_error: string | null;
  created_at: string;
  items: InvoiceItem[];
}

/** Current workspace subscription state, shared across the shell. */
@Injectable({ providedIn: 'root' })
export class SubscriptionService {
  private api = inject(ApiService);

  private _snapshot = signal<SubscriptionSnapshot | null>(null);
  private _loaded = signal(false);

  readonly snapshot = this._snapshot.asReadonly();
  readonly loaded = this._loaded.asReadonly();

  readonly readOnly = computed(() => this._snapshot()?.read_only ?? false);
  readonly isOwned = computed(() => this._snapshot()?.is_owned ?? false);

  /** Fetch the current workspace snapshot. `force` is accepted for symmetry; the
   * call is cheap so it always refetches and updates the shared signal. */
  load(_force = false): Observable<ApiResponse<SubscriptionSnapshot>> {
    return this.api.get<SubscriptionSnapshot>('/billing/plan').pipe(
      tap((res) => {
        if (res.status && res.data) {
          this._snapshot.set(res.data);
        }
        this._loaded.set(true);
      }),
    );
  }

  refresh(): void {
    this.load(true).subscribe();
  }

  plans(): Observable<ApiResponse<SubscriptionPlan[]>> {
    return this.api.get<SubscriptionPlan[]>('/billing/plans');
  }

  invoices(): Observable<ApiResponse<{ invoices: Invoice[] }>> {
    return this.api.get<{ invoices: Invoice[] }>('/billing/invoices');
  }

  /** Unauthenticated plan catalogue for the public marketing homepage. */
  publicPlans(): Observable<ApiResponse<{ individual: SubscriptionPlan[]; company: SubscriptionPlan[] }>> {
    return this.api.get<{ individual: SubscriptionPlan[]; company: SubscriptionPlan[] }>('/public/plans');
  }
}
